import { createApiClient } from '@tms/api-client';

import { getAccessToken } from '../auth/sessionStore';
import { apiBaseUrl } from './baseUrl';
import { refreshSession } from './refresh';

/**
 * The one API client the user app has.
 *
 * **This directory is the only place an API path is written.** Features call the named functions in
 * `identity.ts` and never a path, for the same reason `routes.ts` holds every route path (`CR-006`): a
 * path typed at a call site is a path that can be typed wrong. The protection here is stronger than a
 * convention, because the generated `paths` type means a wrong API path does not compile.
 */
export const api = createApiClient({
  baseUrl: apiBaseUrl(),
  getAccessToken,
  fetch: fetchWithRefresh,
});

/**
 * Anything under here authenticates by cookie or by nothing at all. A `401` from one of them is an
 * answer, not an expired token — refreshing after a wrong password would turn one refusal into two
 * requests and tell the person nothing new.
 */
const AUTH_PREFIX = '/v1/auth/';

/**
 * Every request the app makes, with one silent retry when the access token has lapsed.
 *
 * The token lives fifteen minutes (`FEAT-003 §5.2`) and the app is a phone app people leave open, so a
 * `401` mid-session is ordinary rather than exceptional. What must not happen is a storm: `refreshSession`
 * is single-flight and latches on refusal, and **this retries once and only once** — the second response
 * is returned whatever it says. There is no path here that can produce a third request.
 */
async function fetchWithRefresh(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  // Cloned *before* the body is spent, because the retry needs a request that has not been read. A
  // `Request` whose body has been consumed cannot be cloned at all.
  const spare = input instanceof Request ? input.clone() : null;

  const response = await globalThis.fetch(input, init);
  if (response.status !== 401 || isAuthEndpoint(input)) {
    return response;
  }

  const refreshed = await refreshSession();
  if (!refreshed) {
    return response;
  }

  const token = getAccessToken();
  if (spare && token) {
    spare.headers.set('Authorization', `Bearer ${token}`);
    return globalThis.fetch(spare);
  }

  return globalThis.fetch(input, init);
}

function isAuthEndpoint(input: RequestInfo | URL): boolean {
  const url = input instanceof Request ? input.url : String(input);

  return url.includes(AUTH_PREFIX);
}
